import * as nestsService from '../../../services/Nests/nestsService';
import * as rollsService from '../../../services/Rolls/rollsService';
import { api as kittingViewApi }   from '../KittingView/kittingViewActions.js';
import { api as locationPageApi } from '../locationPageActions.js';
import { api as messageDialogApi }  from '../../MessageDialog/messageDialogActions.js';
import  { api as cancelCutReportDialogApi } from './cancelCutReportDialogActions.js';
import Network from 'infrastructure/js/modules/network';
import { change, clearSubmitErrors, stopSubmit } from 'redux-form';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import  * as dialogHelper  from 'infrastructure/js/components/Dialog/dialogHelper';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import * as daHelper from '../../Dialogs/dialogActionsHelper';
import {dialogsNames} from '../../../enums/dialogsNames';
import {enumTypes, getEnumValue} from '../../../utils/enumHelper';
import * as sapVerificationHelper from '../../../utils/sapVerificationHelper';

const config = {
  getSubmitMethod: () => null, //set null when override the dialogActionsHelper's submit() method
};

export const actionTypes = {
  ...daHelper.getActionTypes(dialogsNames.REPORT_CUT_DIALOG),
  REPORT_CUT_DIALOG_ROLL_CHANGED: 'REPORT_CUT_DIALOG_ROLL_CHANGED',
  REPORT_CUT_DIALOG_SAP_VERIFICATION_FAILED: 'REPORT_CUT_DIALOG_SAP_VERIFICATION_FAILED',
  REPORT_CUT_DIALOG_SAP_VERIFICATION_IGNORED: 'REPORT_CUT_DIALOG_SAP_VERIFICATION_IGNORED'
};

const actions = {
  ...daHelper.getActions(dialogsNames.REPORT_CUT_DIALOG),
  rollChanged: function (payload){
    return {type: actionTypes.REPORT_CUT_DIALOG_ROLL_CHANGED, payload}
  },
  sapVerificationFailed: function (){
    return {type: actionTypes.REPORT_CUT_DIALOG_SAP_VERIFICATION_FAILED}
  },
  sapVerificationIgnored: function (){
    return {type: actionTypes.REPORT_CUT_DIALOG_SAP_VERIFICATION_IGNORED}
  }
};

/******************************************************************************/
// SAP verification (SAAS-25063).

// All of this dialog's verification wording lives under one prefix, because
// dialogHelper.getValidationArea resolves every entry it is handed through 'mat.validations.'.
// That includes the per-item keys the server chooses (validation.error.sap.verification.*), which
// are deliberately namespace-free for exactly this reason.
const SAP_NOT_AVAILABLE_ERROR_KEY = 'sap.verification.not.available';

const SAP_FALLBACK_ERROR_KEY = 'sap.verification.error';

const LABEL_PARAM_ROLL = 'rollBusinessId';

// One confirmation can legitimately cover several rolls of the same work order, so the label's roll
// param may name more than one.
const ROLL_SEPARATOR = ', ';

/**
 * The reduxForm name of the dialog this file drives, imported by the component rather than written
 * twice: marking individual rows as rejected goes through redux-form's own submit-error channel,
 * which is addressed by form name, and a typo there would fail silently.
 */
export const REPORT_CUT_DIALOG_FORM = 'reportCutDialog';

export let api = {
  ...daHelper.getApiActions(actions, config),

  fetchDialogData(dispatch, getState) {
    return function () {
      let data = {
        businessId: '',
        maxPageSize: 1000000,
        active: true,
      };

      let promiseFetchNests = nestsService.fetchNests(data);

      return Promise.all([promiseFetchNests]).then((allResults) => {
        if (!Network.isResponseValid(allResults[0])) {
          console.error('Fetch Report cut data failed');
          messageDialogApi.responseError(dispatch, getState)(null);
          return {success: false};
        }

        let nests = allResults[0].dataList.map((item, index) => {
          return item;
        });

        let nestsOptions = nests.map((item, index) => {
          return {label: item.businessId, value: item.id};
        });

        let dialogData = {nestsOptions};
        dispatch(actions.fetchDialogDataReady(dialogData));

        return {success: true};
      });
    }
  },

  // Nothing is reported until every confirmation number in the dialog has been confirmed by SAP -
  // or the operator has explicitly overridden a failure (SAAS-25063).
  //
  // Gated on the org preference alone, exactly as the Import Cut Plan wizard gates itself. An org
  // without the setting falls straight through to reportCuts, so the feature is invisible to
  // everyone else, and the reporting request itself is unchanged either way: the confirmation
  // number still travels as the cut's note, which is what the ON_CUT_REPORTED hook already sends.
  //
  // Verification runs before the cut exists on purpose. Reporting first and validating afterwards
  // would leave a cut behind that SAP has rejected, and unwinding one is not something an operator
  // can be asked to do at a cutting station.
  submit (dispatch, getState) {
    return function(data, messageDialogBuilder) {

      // Every attempt starts with a clean slate, so the rows marked rejected always reflect the most
      // recent verification rather than a stale mix of it and the one before. The dialog's onChange
      // clears them too - this covers the submit that follows no edit at all.
      dispatch(clearSubmitErrors(REPORT_CUT_DIALOG_FORM));

      if (!PermissionManager.getOrgPreferences().sapVerificationEnabled) {
        return api.reportCuts(dispatch, getState)(data, messageDialogBuilder);
      }

      // The override is spent the moment it is used, and the footer checkbox is handed back to the
      // server's validation warnings (SAAS-25304). Left ticked and still labelled as the SAP
      // override, it swallowed the "continue with warnings" choice: a roll with an alert came back
      // with warnings, and every resubmit went out with ignoreValidationWarnings still false.
      if (data.takeSapVerificationOverride) {
        dispatch(actions.sapVerificationIgnored());
        dispatch(change(REPORT_CUT_DIALOG_FORM, 'footerValidationCheckbox', false));
      }

      if (data.ignoreSapVerification) {
        return api.reportCuts(dispatch, getState)(data, messageDialogBuilder);
      }

      dispatch(actions.setBusy(true));

      return api.sapVerification(dispatch, getState)(data.sapVerificationItems).then((response) => {

        if (sapVerificationHelper.isVerified(response)) {
          return api.reportCuts(dispatch, getState)(data, messageDialogBuilder);
        }

        dispatch(actions.setValidationWarningsData(_buildSapValidations(response, data.sapVerificationItems)));
        dispatch(actions.sapVerificationFailed());

        // Marks the offending Confirmation Number fields themselves, on top of the summary above.
        // The dialog scrolls once there are more than a couple of rolls, so a list of sentences at
        // the bottom does not, on its own, point at the field to fix.
        let fieldErrors = _buildSapFieldErrors(response, data.sapVerificationItems);

        if (fieldErrors) {
          dispatch(stopSubmit(REPORT_CUT_DIALOG_FORM, fieldErrors));
        }

        dispatch(actions.setBusy(false));

        console.error('Report Cut SAP verification failed', response);
        return {success: false};
      });
    };
  },

  sapVerification (dispatch, getState) {
    return function (sapVerificationItems) {

      // rollBusinessId is ours, for naming the failing row back to the operator - it is not part of
      // the server's request model, so it is dropped here rather than shipped and ignored.
      let items = (sapVerificationItems || []).map((item) => ({
        barcode: item.barcode,
        woBusinessId: item.woBusinessId,
        nestBusinessId: item.nestBusinessId,
      }));

      return rollsService.reportCutSapVerification({items});
    }
  },

  // The original report-cut request, unchanged. A named api action rather than a step inside
  // submit(), so the verification gate has exactly one thing to call once it is satisfied, and so
  // the verified and the not-applicable paths are visibly the same call.
  reportCuts (dispatch, getState) {
    return function(data, messageDialogBuilder) {

      // Inject locationId to all items in the request.
      // (The current server API needs it in this format).
      let locationId = locationPageApi.getLocationId(dispatch, getState)();

      data.cutsToReport.forEach((cutToReport) => {
        cutToReport.locationId = locationId;
      });

      dispatch(actions.setBusy(true));

      let locationDetails = locationPageApi.getLocationDetails(dispatch, getState)();

      let newData = {
        operationId: _getOperationIdByType(getEnumValue(enumTypes.OPERATION_TYPE)('CUT'), locationDetails?.operations ),
        reportedCuts: data.cutsToReport,
        ignoreValidationWarnings: data.ignoreValidationWarnings
      };

      let method =  PermissionManager.isWeightSupported() ? rollsService.reportCutsByWeight : rollsService.reportCuts;
      method(locationId, newData).then((response) => {

        if (!Network.isResponseValid(response)) {
          console.error('Report Cut Failed');
          dispatch(actions.hide());
          messageDialogApi.responseError(dispatch, getState)(response);
          return {success: false};
        }


        let validations = dialogHelper.getResponseValidationDetails(response);
        if (validations){
          dispatch(actions.setValidationWarningsData(validations));
          dispatch(actions.setBusy(false));
          return {success: false};
        }

        // Refresh everything.
        locationPageApi.reload(dispatch, getState)().then((promiseObjects) => {

          function showConfirmationDialog() {
            dispatch(actions.hide());
            // Prepare and open Confirmation Message Dialog.
            //Note: workaround for the failed cuts - to fill the missing rollBusinessId
            response.dataList.forEach((item) => {
              if (item && item.data && item.data.rollId && !item.data.rollBusinessId) {
                item.data.rollBusinessId = _getBusinessIdById(item.data.rollId, data.rollsIdsData)
              }
              return item;
            });
            if (messageDialogBuilder) {
              let messageDialogDescriptor = messageDialogBuilder(
                response.dataList,
                messageDialogApi.close(dispatch, getState),
                api.kitThis(dispatch, getState)
              );

              messageDialogApi.open(dispatch, getState)(messageDialogDescriptor);
            }
          }

          if (promiseObjects.kittingView) {
            promiseObjects.kittingView.then(() => {
              showConfirmationDialog();
            });
            return;
          }
          showConfirmationDialog();
        });
      });
    };
  },

  kitThis(dispatch, getState) {
    return (cutId) => {
      messageDialogApi.close(dispatch, getState)();

      // Open Kits tab.
      locationPageApi.activateTab(dispatch, getState)('kitting');

      // Set Current Cut To Kit Dropdown value in the Kits tab.
      kittingViewApi.setCurrentCutToKitDropdownByCutId(dispatch, getState)(cutId);
    }
  },

  onRollChanged(dispatch, getState){
    return (rollData) => {
      dispatch(actions.rollChanged(rollData))
    }
  }
};

export let jsxActions = {
  ...daHelper.getJsxActions(api),

  onRollChanged(rollData) {
    return function (dispatch, getState) {
      api.onRollChanged(dispatch, getState)(rollData)
    }
  },

  onCancelCutReportClick(cut) {
    return function(dispatch, getState) {
      cancelCutReportDialogApi.openDialog(dispatch, getState)(cut);
    }
  },
}


////////////////////////////////////////////////////////////////

/**
 * A failed SAP verification, in the shape the dialog's own validation area already understands
 * (see defaultDialogReducer's SET_VALIDATION_WARNINGS_DATA and dialogHelper.getValidationArea).
 *
 * Reported as errors rather than warnings so each line gets the error icon - these are not
 * borderline conditions, SAP said no - while canIgnoreValidation still offers the override.
 *
 * canIgnoreValidation is unconditionally true, deliberately, and it does double duty: it puts the
 * override checkbox on screen AND keeps hasError false, which is what lets the operator submit
 * again at all. The permission gate is left off to match the Import Cut Plan wizard's footer
 * override - GKN decided any operator who may report a cut may also report it unverified, and a
 * cutting station stopped by an unreachable SAP is the failure mode they were avoiding.
 */
function _buildSapValidations (response, sapVerificationItems) {

  let errors = sapVerificationHelper.buildFailureEntries(response, {
    notAvailableErrorKey: SAP_NOT_AVAILABLE_ERROR_KEY,
    fallbackErrorKey: SAP_FALLBACK_ERROR_KEY,
    decorateParams: (item, params) => ({
      ...params,
      [LABEL_PARAM_ROLL]: _findRollBusinessIds(item, sapVerificationItems),
    }),
  });

  return {
    errors,
    warnings: null,
    canIgnoreValidation: true,
    hasHighSeverityWarning: false,
    hasMediumSeverityWarning: false,
  };
}

/**
 * Which rolls a returned item came from - every one of them, not the first.
 *
 * Needed because the rolls in one dialog are unrelated to each other, so "confirmation number
 * 12345 is not valid" does not say which section to fix - and the response carries no roll of its
 * own (SapVerificationKitItemResponseModel has barcode, work order and nest only).
 *
 * <b>Several rolls legitimately share one (number, work order) pair.</b> A confirmation covers the
 * work order, not the line, so the dialog allows a number to repeat under one work order - and the
 * server collects those into a Set, posting the confirmation to SAP exactly once. All such rolls
 * therefore carry the same verdict, and naming only the first would send the operator to fix one
 * row while an identical one sits untouched below it.
 *
 * Empty rather than null when nothing matches, so react-intl still renders the message. That branch
 * means our own bug, not operator input - an empty confirmation number is blocked by the field's
 * own validator, so every item we sent had something to correlate on.
 */
function _findRollBusinessIds (item, sapVerificationItems) {
  return _matchingSentItems(item, sapVerificationItems)
           .map((sent) => sent.rollBusinessId)
           .join(ROLL_SEPARATOR);
}

/**
 * The rows to mark rejected, keyed the way redux-form addresses fields: an array positioned by row,
 * so `{dialogItems: [undefined, {notes: '...'}]}` marks the second roll's Confirmation Number only.
 *
 * Returns null when there is nothing row-specific to point at - a transport failure or an
 * unreachable source is not any one field's fault, and reddening every row would say it was.
 */
function _buildSapFieldErrors (response, sapVerificationItems) {

  let failedItems = sapVerificationHelper.getFailedItems(response);

  if (failedItems.length === 0) {
    return null;
  }

  // Short on purpose. On desktop this is a hover popover on the field's error icon and on a touch
  // station it renders inline beside the field, so the full sentence - which names the roll, the
  // number and whatever SAP said - stays in the validation area where there is room for it.
  let message = createLabelHelper('mat.locationpage.view.cutting.reportcutdialog.')
                  .get('sapconfirmationnumber.failed');

  let rowErrors = [];

  failedItems.forEach((item) => {
    _matchingSentItems(item, sapVerificationItems).forEach((sent) => {
      rowErrors[sent.rowIndex] = {notes: message};
    });
  });

  return rowErrors.length > 0 ? {dialogItems: rowErrors} : null;
}

/**
 * The items we sent that a returned item is about.
 *
 * Matched on the values the server echoes rather than by position: it sorts its items by severity,
 * so they are not index-aligned with what we sent. Both sides are trimmed because the gateway trims
 * incoming values, so " 12345 " is sent and "12345" comes back.
 *
 * Several can match: one confirmation legitimately covers every roll of its work order.
 */
function _matchingSentItems (item, sapVerificationItems) {

  return (sapVerificationItems || []).filter((sent) =>
    _trimmed(sent.barcode) === _trimmed(item.barcode) &&
    _trimmed(sent.woBusinessId) === _trimmed(item.woBusinessId));
}

function _trimmed (value) {
  return value ? String(value).trim() : '';
}

function _getBusinessIdById (rollId, rollsIdsData) {
  if (rollsIdsData) {
    let found = rollsIdsData.find((item) => {
      return (item.rollId === rollId);
    });
    return found ? found.rollBusinessId : rollId;
  }
  return rollId;
}

function _getOperationIdByType(operationType, operations ) {
  let found = operations?.find?.((op) => {
    return op.operationType?.name === operationType;
  })
  return found?.id || null;
}
